import { useFormStore } from "@/stores/use-form-store"
import FieldInfoIcon from "../../components/field-info"
import { Checkbox } from "../../components/ui/checkbox"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { cn, parseAttributes } from "../../lib/utils"
import {
	CheckboxConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const CheckboxField = ({
	field,
	form,
	commonAttributes,
}: FieldProps) => {
	const { options, displayStyle } = getCheckboxInputAttributes(
		field.attributes,
	)
	const { label, description, infoIconRequired, infoIconText, infoIconType } =
		commonAttributes
	const isHorizontal = displayStyle === "HORIZONTAL"
	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			control={form.control}
			name={field.field_id}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<div
						className={cn(
							isHorizontal
								? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
								: "",
						)}
					>
						{options &&
							options.map(option => (
								<FormItem
									key={option.value}
									className="flex flex-row items-center gap-3"
								>
									<FormControl>
										<Checkbox
											disabled={
												isVerified || shouldDisable
											}
											checked={formField.value?.includes(
												option.value,
											)}
											onCheckedChange={checked => {
												return checked
													? formField.onChange([
															...(formField?.value ||
																[]),
															option.value,
														])
													: formField.onChange(
															formField.value?.filter(
																(
																	value: string,
																) =>
																	value !==
																	option.value,
															),
														)
											}}
											className="rounded-xs"
										/>
									</FormControl>
									<FormLabel className="text-sm font-normal">
										{option.label}
									</FormLabel>
								</FormItem>
							))}
					</div>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getCheckboxInputAttributes(attributes: Attribute[]) {
	const props: {
		options?: { label: string; value: string }[]
		selectAll?: boolean
		defaultOptions?: string[]
		displayStyle?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case CheckboxConfig.Options:
				props.options = value as { label: string; value: string }[]
				break
			case CheckboxConfig.DisplayStyle:
				props.displayStyle = value as string
				break
		}
	})
	return props
}
